/**
 * "Mark as completed" modal: BO status selection and Comment RPA payload.
 * @module commentRpaNewModal
 */
import { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { Modal } from "@risalabs_frontend_org/oasis-ui-kit";
import { AllCommentsProps } from "../../../data-model/busniessOfficeNote";
import {
  AuthOnFile,
  GeneralOrderDetails,
} from "../../../data-model/medicalPaOrdersModel";
import { getBadgePropsForAuthStatus } from "../../../enums/medicalPaOrdersAuthStatus";
import { ModalId } from "../../../enums/modalId";
import { RootState } from "../../../redux/store/store";
import WarningIcon from "../../../svg/warningIcon";
import { shouldShowReassignmentCheckbox } from "../../../utils/organizationHelper";
import { useLinkedOrdersModalView } from "../../expandableTableForOrderAuth/hooks/useLinkedOrdersModalView";
import OrderReassignmentWithCheckbox from "../../orderAssignmentWithCheckbox/orderReassignmentWithCheckbox";
import TextWithAuthStatus from "../../textWithAuthStatus/TextWithAuthStatus";
import RenderSiblingOrderContentBody from "../updateCigNoteConfirmationDialog/renderSiblingOrderContentBody";
import ProcessingMethodSection from "./components/ProcessingMethodSection";
import { useCommentRpaAnalytics } from "./hooks/useCommentRpaAnalytics";
import { useCommentRpaFormState } from "./hooks/useCommentRpaFormState";
import { useCommentRpaSave } from "./hooks/useCommentRpaSave";

/** Props for the Comment RPA "mark as completed" modal. */
interface CommentRpaNewModalProps {
  onClose: () => void;
  onSave: () => void;
  generalOrderDetails?: GeneralOrderDetails;
  authOnFileDetails?: AuthOnFile;
  wrtiteBackStatus: string;
  docRpaStatus: string;
  id: string;
  orderId?: string;
  writeBackReports?: AllCommentsProps[] | null;
  auth_status?: string;
  initialAuthStatus?: string;
  boStatus?: string | { id: string; text: string };
}

/**
 * Confirms BO status, then persists via {@link useCommentRpaSave} (parallel
 * mark-as-completed for parent and selected linked siblings).
 *
 * @param props - Write-back / document RPA state, identifiers, and callbacks.
 */
const CommentRpaNewModal = (props: CommentRpaNewModalProps) => {
  const authStatusOptions = useSelector(
    (state: RootState) => state.authStatusOptions,
  );
  const resolvedOrderId = props.orderId ?? props.id;
  const defaultBoFromOrder = useMemo(
    () =>
      props.generalOrderDetails?.status?.bo_value ??
      props.generalOrderDetails?.status?.bo_status ??
      undefined,
    [
      props.generalOrderDetails?.status?.bo_value,
      props.generalOrderDetails?.status?.bo_status,
    ],
  );
  const defaultEstimateValueFromOrder = useMemo(
    () => props.generalOrderDetails?.status?.estimate_value ?? undefined,
    [props.generalOrderDetails?.status?.estimate_value],
  );
  const formState = useCommentRpaFormState({
    wrtiteBackStatus: props.wrtiteBackStatus,
    docRpaStatus: props.docRpaStatus,
    boStatus: props.boStatus,
    defaultBoFromOrder,
    defaultEstimateValueFromOrder,
    authOnFile: props.authOnFileDetails,
  });

  const [reassignmentChecked, setReassignmentChecked] = useState(false);
  const [, setProviderSelectionTick] = useState(0);
  const bumpProviderSelection = useCallback(() => {
    setProviderSelectionTick((n) => n + 1);
  }, []);

  const reassignmentBlocksSave =
    reassignmentChecked && !formState.hasProviderSelection();

  const analytics = useCommentRpaAnalytics(
    resolvedOrderId,
    props?.generalOrderDetails?.demographics?.patient_id ??
      props?.generalOrderDetails?.demographics?.patient_mrn ??
      "",
  );

  const computeAuthStatusText = useMemo(() => {
    return getBadgePropsForAuthStatus(
      authStatusOptions.data ?? [],
      props?.auth_status
        ? props?.auth_status
        : (props?.authOnFileDetails?.auth_status ?? ""),
    )?.text;
  }, [
    authStatusOptions.data,
    props?.auth_status,
    props?.authOnFileDetails?.auth_status,
  ]);

  const {
    expandableTableData,
    siblingOrdersError,
    hasLinkedOrders,
    selectedSiblingOrderIds,
  } = useLinkedOrdersModalView();

  const { handleSave } = useCommentRpaSave({
    orderId: resolvedOrderId,
    formState,
    onSave: props.onSave,
    authStatus: props.auth_status,
    authOnFileDetails: props.authOnFileDetails,
    writeBackReports: props.writeBackReports,
    generalOrderDetails: props.generalOrderDetails,
  });

  const handleModalSave = () => {
    handleSave(
      expandableTableData.length > 0 && selectedSiblingOrderIds.length > 0
        ? selectedSiblingOrderIds
        : undefined,
    );
  };

  return (
    <Modal
      dialogId={ModalId.COMMENT_RPA_MODAL_NEW}
      title="Mark as Completed"
      onClose={props.onClose}
      onCancel={props.onClose}
      onSave={handleModalSave}
      disableSave={formState.isLoading || reassignmentBlocksSave}
      saveButtonText={formState.isLoading ? "Saving..." : "Mark as Completed"}
      cancelText="Cancel"
      maxHeightPercentage={80}
    >
      <div className="comment-rpa-new-modal flex flex-col gap-4 px-2 py-3">
        {formState.determineTextForWarningBanner && (
          <div className="gap flex flex-row items-center gap-[0.625rem] bg-[#FAE6E6] p-2">
            <WarningIcon outerFillColor="black" height="16" width="16" />
            <div className="text-sm font-normal text-primaryGray-1">
              You have not triggered the{" "}
              <span className="font-bold">
                {" "}
                {formState.determineTextForWarningBanner}
              </span>
            </div>
          </div>
        )}

        <div className="flex flex-col items-start justify-center gap-5">
          <ProcessingMethodSection
            selectedBadge={formState.selectedBadge}
            setSelectedBadge={formState.setSelectedBadge}
            boStatusOptions={formState.boStatusOptions}
            onDropdownClicked={analytics.logDropdownClicked}
            onDropdownSelected={analytics.logDropdownSelected}
          />
        </div>

        <TextWithAuthStatus status={computeAuthStatusText} />

        {shouldShowReassignmentCheckbox(
          props.authOnFileDetails?.auth_status,
        ) && (
          <OrderReassignmentWithCheckbox
            onReassignmentCheckedChange={setReassignmentChecked}
            onSelectedProviderChange={(provider) => {
              formState.selectedProvider.current = {
                id: provider.id,
                name: provider.name,
              };
              bumpProviderSelection();
            }}
          />
        )}

        {hasLinkedOrders && (
          <RenderSiblingOrderContentBody
            expandedContentType="table"
            expandableTableData={expandableTableData}
            siblingOrdersLoading={false}
            siblingOrdersError={siblingOrdersError}
            descriptionText="Complete following linked orders as well?"
          />
        )}
      </div>
    </Modal>
  );
};

export default CommentRpaNewModal;
